import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { projectApi, customerApi, userApi, taskApi } from '../../api/endpoints.js';
import { useApi } from '../../hooks/useApi.js';
import { useAuth } from '../../auth/AuthContext.jsx';
import { useToast } from '../../hooks/useToast.jsx';
import { useApiError } from '../../hooks/useApiError.js';
import PageHeader from '../../components/PageHeader.jsx';
import DataTable from '../../components/DataTable.jsx';
import Toolbar from '../../components/Toolbar.jsx';
import Badge from '../../components/Badge.jsx';
import Modal from '../../components/Modal.jsx';
import EmptyState from '../../components/EmptyState.jsx';
import { Field, Select, TextArea, TextInput } from '../../components/Field.jsx';
import { formatDate, fullName } from '../../utils/format.js';
import { PROJECT_STATUSES, PROJECT_PRIORITIES } from '../../constants.js';

const EMPTY = {
  customerId: '',
  title: '',
  code: '',
  description: '',
  spec: '',
  status: 'TO_DO',
  priority: 'NORMAL',
  assignedItId: '',
  startAt: '',
  dueAt: '',
};

/** <input type="datetime-local"> wants "YYYY-MM-DDTHH:mm", the API wants ISO. */
const toLocalInput = (value) => value?.slice(0, 16) ?? '';
const toIso = (value) => (value ? new Date(value).toISOString() : null);

const formOf = (project) => ({
  ...project,
  assignedItId: project.assignedItId ?? '',
  spec: project.spec ?? '',
  startAt: toLocalInput(project.startAt),
  dueAt: toLocalInput(project.dueAt),
});

/**
 * THE PROJECT BOARD - what the IT team is delivering.
 *
 * A project is one piece of work on an account: an integration, a migration, a
 * support engagement. An account can carry several, which is why this is its
 * own record rather than a status on the customer.
 *
 * TWO VIEWS OF THE SAME ROWS, and the toggle lives in the URL so a view can be
 * linked to:
 *   - the LIST, for filtering and reading detail;
 *   - the BOARD, six columns you drag a card across to change its status.
 * Dragging is not the only way to move a card - the arrows on each card do the
 * same thing, because a drag is impossible with a keyboard and awkward on a
 * phone.
 *
 * "Mes projets" is ON by default. The question you open this screen with is
 * "what is on my plate"; the whole team's board is one click away.
 *
 * Only signed customers can be picked in the form. That is enforced by the API
 * too - it is repeated here so the dropdown cannot offer something the save
 * would then refuse.
 *
 * There is no delete button on purpose: a project people have worked on is
 * CANCELLED, not erased. A cancellation drops off the board on its own after a
 * week (CANCELLED_VISIBLE_DAYS in the API); the row itself is kept.
 */
const ProjectsPage = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const toast = useToast();
  const toMessage = useApiError();
  const locale = i18n.resolvedLanguage;

  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get('view') === 'board' ? 'board' : 'list';

  // The dashboard tiles link straight into a filtered list, so the filters
  // start from the URL rather than from a fixed default.
  const [query, setQuery] = useState({
    q: '',
    status: searchParams.get('status') ?? '',
    open: searchParams.get('open') ?? '',
    assignedItId: '',
    mine: true,
  });
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newTask, setNewTask] = useState('');
  const [dragging, setDragging] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  const { data, loading, refresh, setData } = useApi(
    () =>
      projectApi.list({
        q: query.q || undefined,
        // The board needs every column at once; a status filter would empty five
        // of the six, so it only applies to the list.
        status: view === 'list' ? query.status || undefined : undefined,
        open: view === 'list' ? query.open || undefined : undefined,
        assignedItId: query.assignedItId || undefined,
        mine: query.mine || undefined,
        pageSize: 200,
      }),
    [query, view],
  );

  // Only accounts sales has signed off: offering the others would build a form
  // whose save is guaranteed to fail.
  const { data: customers } = useApi(() => customerApi.list({ signed: 'yes', pageSize: 200 }), []);
  const { data: directory } = useApi(() => userApi.directory(), []);

  // The open project, loaded on its own so its task list is always current.
  const { data: detail, refresh: refreshDetail } = useApi(
    () => (form?.id ? projectApi.get(form.id) : Promise.resolve(null)),
    [form?.id],
  );

  const itPeople = (directory?.items ?? []).filter((person) => person.role === 'IT');
  const rows = data?.items ?? [];

  // Opened from the dashboard: reopen that project so its detail is on screen.
  useEffect(() => {
    const wanted = searchParams.get('project');
    if (!wanted || form?.id === wanted) return;
    const project = rows.find((row) => row.id === wanted);
    if (project) setForm(formOf(project));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, data]);

  const patchQuery = (part) => setQuery((current) => ({ ...current, ...part }));

  const setView = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'board') params.set('view', 'board');
    else params.delete('view');
    setSearchParams(params, { replace: true });
  };

  const closeForm = () => {
    setForm(null);
    setNewTask('');
    if (searchParams.get('project')) {
      const params = new URLSearchParams(searchParams);
      params.delete('project');
      setSearchParams(params, { replace: true });
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        title: form.title,
        code: form.code,
        description: form.description,
        spec: form.spec,
        status: form.status,
        priority: form.priority,
        assignedItId: form.assignedItId || null,
        startAt: toIso(form.startAt),
        dueAt: toIso(form.dueAt),
      };
      if (form.id) await projectApi.update(form.id, body);
      else await projectApi.create({ ...body, customerId: form.customerId });
      toast.success(t('common.save'));
      closeForm();
      refresh();
    } catch (err) {
      toast.error(toMessage(err));
    } finally {
      setBusy(false);
    }
  };

  /** A drag, or the arrows on a card - both land here. */
  const move = async (project, status) => {
    if (!status || project.status === status) return;

    // Optimistic: the card moves under the cursor and rolls back if the API says no.
    const previous = data;
    setData((current) => ({
      ...current,
      items: current.items.map((row) => (row.id === project.id ? { ...row, status } : row)),
    }));

    try {
      await projectApi.update(project.id, { status });
      toast.success(
        t('projects.moved', { title: project.title, status: t(`projects.status.${status}`) }),
      );
    } catch (err) {
      setData(previous);
      toast.error(toMessage(err));
    }
  };

  /* ---------------- the tasks on the open project ---------------- */

  const addTask = async () => {
    const title = newTask.trim();
    if (!title || !form?.id) return;
    try {
      await taskApi.create({
        title,
        projectId: form.id,
        customerId: form.customerId ?? null,
        assignedTo: user.id,
      });
      setNewTask('');
      refreshDetail();
      refresh();
    } catch (err) {
      toast.error(toMessage(err));
    }
  };

  const toggleTask = async (task) => {
    try {
      await taskApi.update(task.id, { status: task.status === 'DONE' ? 'OPEN' : 'DONE' });
      refreshDetail();
      refresh();
    } catch (err) {
      toast.error(toMessage(err));
    }
  };

  /* ---------------- the list view ---------------- */

  const columns = [
    {
      key: 'title',
      header: t('projects.projectTitle'),
      render: (row) => (
        <div className="cell-stack">
          <span className="cell-strong">{row.title}</span>
          <span className="cell-muted">{row.code || '-'}</span>
        </div>
      ),
    },
    {
      key: 'customerName',
      header: t('customers.companyName'),
      // A link, not a label. The account is where the qualification brief lives -
      // what the client actually asked for - and that is the first thing you
      // need before starting a piece of delivery. Clicking it must not also
      // open the row's edit modal.
      render: (row) =>
        row.customerId ? (
          <Link
            to={`/customers/${row.customerId}`}
            className="cell-link"
            onClick={(event) => event.stopPropagation()}
          >
            {row.customerName ?? '-'}
          </Link>
        ) : (
          '-'
        ),
    },
    {
      key: 'assignee',
      header: t('projects.assignedTo'),
      render: (row) => (row.assignee ? fullName(row.assignee) : t('projects.unassigned')),
    },
    {
      key: 'status',
      header: t('common.status'),
      render: (row) => <Badge value={row.status} label={t(`projects.status.${row.status}`)} />,
    },
    {
      key: 'dueAt',
      header: t('projects.dueAt'),
      render: (row) =>
        row.dueAt ? (
          row.overdue ? (
            <Badge value="LOST" label={formatDate(row.dueAt, locale)} />
          ) : (
            formatDate(row.dueAt, locale)
          )
        ) : (
          '-'
        ),
    },
    {
      key: 'tasks',
      header: t('projects.openTasks'),
      className: 'cell-right',
      render: (row) => `${row.taskCounts.open} / ${row.taskCounts.total}`,
    },
  ];

  const customerOptions = (customers?.items ?? []).map((customer) => ({
    value: customer.id,
    label: customer.companyName,
  }));

  const tasks = detail?.tasks ?? [];

  return (
    <>
      <PageHeader
        title={t('projects.title')}
        subtitle={t('projects.subtitle')}
        actions={
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => setForm({ ...EMPTY })}
            disabled={!customerOptions.length}
          >
            {t('projects.newProject')}
          </button>
        }
      />

      <Toolbar>
        <div className="segmented" role="group">
          {['list', 'board'].map((option) => (
            <button
              key={option}
              type="button"
              className={view === option ? 'segmented__btn segmented__btn--on' : 'segmented__btn'}
              onClick={() => setView(option)}
            >
              {t(option === 'list' ? 'projects.viewList' : 'projects.viewBoard')}
            </button>
          ))}
        </div>

        <TextInput
          value={query.q}
          onChange={(value) => patchQuery({ q: value })}
          placeholder={t('common.search')}
          aria-label={t('common.search')}
        />

        {/* The board shows every column by construction, so the status filter
            only makes sense on the list. */}
        {view === 'list' ? (
          <Select
            value={query.status}
            onChange={(value) => patchQuery({ status: value, open: '' })}
            placeholder={t('common.all')}
            options={PROJECT_STATUSES.map((status) => ({
              value: status,
              label: t(`projects.status.${status}`),
            }))}
          />
        ) : null}

        <Select
          value={query.assignedItId}
          onChange={(value) => patchQuery({ assignedItId: value, mine: false })}
          placeholder={t('projects.everyone')}
          options={itPeople.map((person) => ({ value: person.id, label: fullName(person) }))}
        />

        {/* On by default: you open this screen to see your own plate. */}
        <label className={query.mine ? 'option option--checked' : 'option'}>
          <input
            type="checkbox"
            checked={query.mine}
            onChange={() => patchQuery({ mine: !query.mine, assignedItId: '' })}
          />
          <span>{t('projects.mine')}</span>
        </label>

        <span className="toolbar__count">
          {rows.length} {t('common.results')}
        </span>
      </Toolbar>

      {view === 'board' ? (
        <div className="kanban">
          {PROJECT_STATUSES.map((status) => {
            const column = rows.filter((row) => row.status === status);
            const index = PROJECT_STATUSES.indexOf(status);

            return (
              <section
                key={status}
                className={
                  dropTarget === status ? 'kanban__column kanban__column--over' : 'kanban__column'
                }
                onDragOver={(event) => {
                  event.preventDefault();
                  setDropTarget(status);
                }}
                onDragLeave={() => setDropTarget((current) => (current === status ? null : current))}
                onDrop={(event) => {
                  event.preventDefault();
                  setDropTarget(null);
                  if (dragging) move(dragging, status);
                  setDragging(null);
                }}
              >
                <header className="kanban__header">
                  <span>{t(`projects.status.${status}`)}</span>
                  <span className="kanban__count">{column.length}</span>
                </header>
                {status === 'CANCELLED' ? (
                  <p className="kanban__value">{t('projects.cancelledWindow')}</p>
                ) : null}

                <div className="kanban__cards">
                  {column.map((project) => {
                    const prev = PROJECT_STATUSES[index - 1];
                    const next = PROJECT_STATUSES[index + 1];

                    return (
                      <article
                        key={project.id}
                        className="kanban__card"
                        draggable
                        onDragStart={() => setDragging(project)}
                        onDragEnd={() => {
                          setDragging(null);
                          setDropTarget(null);
                        }}
                      >
                        <button
                          type="button"
                          className="kanban__card-main"
                          onClick={() => setForm(formOf(project))}
                        >
                          <span className="kanban__card-title">{project.title}</span>
                          <span className="kanban__card-meta">
                            {project.customerName ?? '-'}
                            {project.assignee ? ` - ${fullName(project.assignee)}` : ''}
                          </span>
                          <span className="kanban__card-meta">
                            {project.dueAt ? formatDate(project.dueAt, locale) : ''}
                            {project.taskCounts.total
                              ? ` - ${project.taskCounts.open}/${project.taskCounts.total}`
                              : ''}
                          </span>
                          {project.overdue ? <Badge value="LOST" label={t('projects.overdue')} /> : null}
                        </button>

                        {/* Keyboard- and touch-friendly alternative to dragging */}
                        <div className="kanban__card-actions">
                          <button
                            type="button"
                            className="btn btn--ghost btn--small"
                            disabled={!prev}
                            onClick={() => move(project, prev)}
                            aria-label={t('projects.moveLeft')}
                          >
                            &#8592;
                          </button>
                          <button
                            type="button"
                            className="btn btn--ghost btn--small"
                            disabled={!next}
                            onClick={() => move(project, next)}
                            aria-label={t('projects.moveRight')}
                          >
                            &#8594;
                          </button>
                        </div>
                      </article>
                    );
                  })}
                  {!column.length ? <p className="kanban__empty">-</p> : null}
                </div>
              </section>
            );
          })}
        </div>
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          loading={loading}
          emptyTitle={t('projects.empty')}
          emptyDescription={t('projects.emptyHint')}
          onRowClick={(row) => setForm(formOf(row))}
        />
      )}

      {!customerOptions.length ? (
        <EmptyState
          title={t('projects.noSignedCustomer')}
          description={t('projects.noSignedCustomerHint')}
        />
      ) : null}

      <Modal
        open={Boolean(form)}
        title={form?.id ? form.title : t('projects.newProject')}
        onClose={closeForm}
        footer={
          <>
            <button type="button" className="btn" onClick={closeForm}>
              {t('common.cancel')}
            </button>
            <button type="button" className="btn btn--primary" onClick={save} disabled={busy}>
              {busy ? t('common.saving') : t('common.save')}
            </button>
          </>
        }
      >
        {form ? (
          <div className="form-grid">
            <div className="span-2">
              <Field label={t('projects.projectTitle')} htmlFor="p-title" required>
                <TextInput id="p-title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
              </Field>
            </div>

            {/* The account is chosen once. Moving delivery work from one customer
                to another is not an edit, it is a different project. */}
            <Field label={t('customers.companyName')} htmlFor="p-customer" required>
              {form.id ? (
                <>
                  <p className="static-value">
                    {form.customerId ? (
                      <Link to={`/customers/${form.customerId}`} className="cell-link">
                        {form.customerName ?? '-'}
                      </Link>
                    ) : (
                      '-'
                    )}
                  </p>
                  <p className="field__hint">{t('projects.openCustomerHint')}</p>
                </>
              ) : (
                <Select
                  id="p-customer"
                  value={form.customerId}
                  onChange={(v) => setForm({ ...form, customerId: v })}
                  placeholder={t('common.none')}
                  options={customerOptions}
                />
              )}
            </Field>
            <Field label={t('projects.code')} htmlFor="p-code" hint={t('projects.codeHint')}>
              <TextInput id="p-code" value={form.code} onChange={(v) => setForm({ ...form, code: v })} />
            </Field>

            <Field label={t('projects.assignedTo')} htmlFor="p-owner">
              <Select
                id="p-owner"
                value={form.assignedItId}
                onChange={(v) => setForm({ ...form, assignedItId: v })}
                placeholder={t('projects.unassigned')}
                options={itPeople.map((person) => ({ value: person.id, label: fullName(person) }))}
              />
            </Field>
            <Field label={t('common.status')} htmlFor="p-status">
              <Select
                id="p-status"
                value={form.status}
                onChange={(v) => setForm({ ...form, status: v })}
                options={PROJECT_STATUSES.map((status) => ({
                  value: status,
                  label: t(`projects.status.${status}`),
                }))}
              />
            </Field>

            <Field label={t('projects.priority')} htmlFor="p-prio">
              <Select
                id="p-prio"
                value={form.priority}
                onChange={(v) => setForm({ ...form, priority: v })}
                options={PROJECT_PRIORITIES.map((p) => ({ value: p, label: t(`tasks.priorities.${p}`) }))}
              />
            </Field>
            <Field label={t('projects.startAt')} htmlFor="p-start">
              <TextInput
                id="p-start"
                type="datetime-local"
                value={form.startAt}
                onChange={(v) => setForm({ ...form, startAt: v })}
              />
            </Field>
            <Field label={t('projects.dueAt')} htmlFor="p-due">
              <TextInput
                id="p-due"
                type="datetime-local"
                value={form.dueAt}
                onChange={(v) => setForm({ ...form, dueAt: v })}
              />
            </Field>

            <div className="span-2">
              <Field label={t('projects.summary')} htmlFor="p-desc">
                <TextArea
                  id="p-desc"
                  rows={2}
                  value={form.description}
                  onChange={(v) => setForm({ ...form, description: v })}
                />
              </Field>
            </div>

            {/* THE CAHIER DES CHARGES. The customer file says what the client
                asked for; this says what we agreed to build - scope, steps,
                acceptance criteria, constraints. One per project, because a
                customer carrying two projects has two different jobs to do. */}
            <div className="span-2">
              <Field label={t('projects.spec')} htmlFor="p-spec" hint={t('projects.specHint')}>
                <TextArea
                  id="p-spec"
                  rows={14}
                  value={form.spec}
                  onChange={(v) => setForm({ ...form, spec: v })}
                />
              </Field>
            </div>

            {/* The work, on the thing it belongs to. Ticking a box here is the
                same write as ticking it on the Tasks screen. */}
            {form.id ? (
              <div className="span-2">
                <Field label={t('projects.taskList')} hint={t('projects.taskListHint')}>
                  <ul className="task-list task-list--inline">
                    {tasks.map((task) => (
                      <li key={task.id} className={task.overdue ? 'task task--overdue' : 'task'}>
                        <input
                          type="checkbox"
                          checked={task.status === 'DONE'}
                          onChange={() => toggleTask(task)}
                          aria-label={t('tasks.statuses.DONE')}
                        />
                        <div className="task__body">
                          <span
                            className={
                              task.status === 'DONE' ? 'task__title task__title--done' : 'task__title'
                            }
                          >
                            {task.title}
                          </span>
                          <span className="task__meta">
                            {task.assignee ? fullName(task.assignee) : ''}
                            {task.dueAt ? ` - ${formatDate(task.dueAt, locale)}` : ''}
                          </span>
                        </div>
                      </li>
                    ))}
                    {!tasks.length ? <li className="kanban__empty">{t('tasks.empty')}</li> : null}
                  </ul>

                  <div className="inline-add">
                    <TextInput
                      value={newTask}
                      onChange={setNewTask}
                      placeholder={t('projects.addTaskPlaceholder')}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          addTask();
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn--small"
                      onClick={addTask}
                      disabled={!newTask.trim()}
                    >
                      {t('projects.addTask')}
                    </button>
                  </div>
                </Field>
              </div>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </>
  );
};

export default ProjectsPage;
